"use client";
import React, { useRef, useState } from "react";
import { Switch, message } from "antd";
import { MessageOutlined, PictureOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./AskQuestionBox.css";

export default function AskQuestionBox({ onSubmit, onAttach }) {
  const { t } = useTranslation();

  const [question, setQuestion] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false); // ✅ même nom et logique
  const [attached, setAttached] = useState([]); // [{file, url}]
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async () => {
    if (!question.trim() && attached.length === 0) {
      message.warning(t("questions.askBox.warnings.empty"));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        description: question.trim(),
        isAnonymous, // ✅ même clé que sur le backend
        files: attached.map((a) => a.file),
      };
      const accepted = await onSubmit?.(payload);
      if (accepted !== true) return;

      setQuestion("");
      setAttached([]);
      setIsAnonymous(false);
    } catch {
      message.error(t("questions.askBox.errors.submit"));
    } finally {
      setLoading(false);
    }
  };

  const openPicker = () => fileRef.current?.click();

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
    }));
    setAttached((prev) => [...prev, ...next]);
    onAttach?.(files);
    e.target.value = "";
  };

  const removeAttachment = (idx) => {
    setAttached((prev) => {
      const cp = [...prev];
      const [rm] = cp.splice(idx, 1);
      if (rm?.url) URL.revokeObjectURL(rm.url);
      return cp;
    });
  };

  return (
    <div className="aqb-container">
      {/* Header */}
      <div className="aqb-header">
        <div className="aqb-label">
          <MessageOutlined className="aqb-icon" />
          <span>{t("questions.askBox.title")}</span>
        </div>

        <div className="aqb-anon">
          <span>{t("questions.askBox.anonymous")}</span>
          <Switch
            checked={isAnonymous}
            onChange={setIsAnonymous}
            size="small"
            className="aqb-switch"
            disabled={loading}
            aria-label={t("questions.askBox.aria.anonymous")}
          />
        </div>
      </div>

      {/* Input + icon image */}
      <div className="aqb-body">
        <div className="aqb-inputwrap">
          <input
            type="text"
            className="aqb-input"
            placeholder={t("questions.askBox.placeholder")}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
          />

          <button
            type="button"
            className="aqb-attachbtn"
            aria-label={t("questions.askBox.aria.attach")}
            title={t("questions.askBox.aria.attach")}
            onClick={openPicker}
            disabled={loading}
          >
            <PictureOutlined />
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="aqb-file"
            onChange={handleFiles}
          />
        </div>

        <button
          className="aqb-btn"
          onClick={handleSubmit}
          disabled={loading || (!question.trim() && attached.length === 0)}
        >
          {loading ? t("questions.askBox.actions.publishing") : t("questions.askBox.actions.publish")}
        </button>
      </div>

      {/* Preview of attached images */}
      {attached.length > 0 && (
        <div className="aqb-attached">
          {attached.map((a, i) => (
            <div className="aqb-thumb" key={i}>
              <img src={a.url} alt={t("questions.askBox.aria.attachmentAlt", { index: i + 1 })} />
              <button
                type="button"
                className="aqb-thumb-x"
                onClick={() => removeAttachment(i)}
                aria-label={t("questions.askBox.aria.remove")}
                title={t("questions.askBox.aria.remove")}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
